import { Link } from "react-router-dom";
import { GraduationCap, ArrowRight } from "lucide-react";
import { feeRange } from "@/lib/api";

const BADGE_PALETTE = [
  { bg: "#C85A32", fg: "#FDFBF7" },
  { bg: "#1E3A2B", fg: "#DAA520" },
  { bg: "#DAA520", fg: "#1C1917" },
  { bg: "#2A4E3B", fg: "#FDFBF7" },
  { bg: "#A84624", fg: "#FDFBF7" },
];

function monogram(name) {
  if (!name) return "SC";
  const words = name
    .replace(/[^\w\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w && !["the", "and", "of", "for"].includes(w.toLowerCase()));
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return words[0]?.slice(0, 2).toUpperCase() || "SC";
}

function RankedSchoolRow({ school }) {
  const palette = BADGE_PALETTE[(school.rank - 1) % BADGE_PALETTE.length];
  const slug = (school.slug || school.id || "school").replace(
    /[^a-z0-9-]/gi,
    "-"
  );
  // Simple tag inference for display chips (factual metadata)
  const tags = [];
  const desc = (school.description || "").toLowerCase();
  if (desc.includes("british") || desc.includes("cambridge") || desc.includes("igcse")) {
    if (desc.includes("british")) tags.push("British");
    if (desc.includes("cambridge")) tags.push("Cambridge");
    if (desc.includes("igcse")) tags.push("IGCSE");
    if (desc.includes("a-level")) tags.push("A-Level");
  }
  if (desc.includes("american") || desc.includes("international baccalaureate") || desc.includes(" ib ")) {
    tags.push("American");
    if (desc.includes("baccalaureate") || desc.includes(" ib ")) tags.push("IB");
  }
  if (desc.includes("nigerian")) tags.push("Nigerian");
  if (desc.includes("stem") || desc.includes("science-focused")) tags.push("STEM");
  if (desc.includes("boarding")) tags.push("Boarding");
  const uniqueTags = [...new Set(tags)].slice(0, 4);

  return (
    <article
      data-testid={`ranked-${school.rank}`}
      className="group relative bg-white border border-stone-200 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row gap-5 hover:border-[#C85A32]/50 hover:shadow-[0_12px_32px_-14px_rgba(200,90,50,0.2)] transition-all"
    >
      {/* Rank number */}
      <div className="flex sm:flex-col items-center sm:items-start gap-4 sm:gap-3 shrink-0">
        <div
          className="font-display font-bold text-5xl sm:text-6xl leading-none tabular-nums"
          style={{ color: "#1E3A2B", letterSpacing: "-0.04em" }}
          aria-hidden
        >
          {String(school.rank).padStart(2, "0")}
        </div>
        <div
          className="flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl font-display font-bold text-lg sm:text-xl shrink-0 shadow-inner"
          style={{ backgroundColor: palette.bg, color: palette.fg }}
          aria-hidden
        >
          {monogram(school.name)}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col sm:flex-row gap-5">
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h3
                className="font-display text-xl sm:text-2xl font-bold text-stone-900 leading-tight group-hover:text-[#C85A32] transition-colors"
              >
                {school.name}
              </h3>
              <div className="mt-1 text-sm text-stone-500">
                <span>{school.district}</span>
                {school.year_established ? (
                  <>
                    <span className="mx-2 text-stone-300">·</span>
                    <span>Since {school.year_established}</span>
                  </>
                ) : null}
                <span className="mx-2 text-stone-300">·</span>
                <span>{feeRange(school)}/yr</span>
              </div>
            </div>
            {school.rank === 1 && (
              <span className="inline-flex items-center gap-1 bg-[#DAA520] text-stone-900 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full">
                <GraduationCap className="w-3 h-3" /> No. 1 for 2026
              </span>
            )}
          </div>

          <p className="mt-3 text-stone-700 leading-relaxed text-sm sm:text-base line-clamp-3">
            {school.description}
          </p>

          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-wrap gap-1.5">
              {uniqueTags.map((t) => (
                <span
                  key={t}
                  className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-stone-100 text-stone-700 uppercase tracking-wider"
                >
                  {t}
                </span>
              ))}
              {(school.levels || []).slice(0, 3).map((lvl) => (
                <span
                  key={lvl}
                  className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-[#1E3A2B]/5 text-[#1E3A2B]"
                >
                  {lvl}
                </span>
              ))}
            </div>
            <Link
              to={`/schools/${school.slug || school.id}`}
              data-testid={`ranked-read-more-${slug}`}
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#C85A32] hover:text-[#A84624]"
            >
              Read more
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Campus photo (desktop only, aspect square) */}
        {school.image_url ? (
          <Link
            to={`/schools/${school.slug || school.id}`}
            className="hidden md:block relative w-40 h-28 lg:w-48 lg:h-32 rounded-xl overflow-hidden shrink-0 bg-stone-100"
            aria-hidden
            tabIndex={-1}
          >
            <img
              src={school.image_url}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </Link>
        ) : null}
      </div>
    </article>
  );
}

export default RankedSchoolRow;
